/** Campos que a busca livre olha e o card não mostra como estão (RPC search_freelancers_match_reasons). */
export enum SearchMatchField {
  HistoriaProfissional = "historia_profissional",
  ExperienciasAnteriores = "experiencias_anteriores",
  Objetivos = "objetivos",
  Email = "email",
  Telefone = "telefone",
  Cidade = "cidade",
  Idiomas = "idiomas",
  TipoAtuacao = "tipo_atuacao",
  LinkedinBio = "linkedin_bio",
  LinkedinExperiencias = "linkedin_experiencias",
  LinkedinSkills = "linkedin_skills",
  Portfolio = "portfolio",
}

export const SEARCH_MATCH_FIELD_LABEL: Record<SearchMatchField, string> = {
  [SearchMatchField.HistoriaProfissional]: "História profissional",
  [SearchMatchField.ExperienciasAnteriores]: "Experiências anteriores",
  [SearchMatchField.Objetivos]: "Objetivos",
  [SearchMatchField.Email]: "E-mail",
  [SearchMatchField.Telefone]: "Telefone",
  [SearchMatchField.Cidade]: "Cidade",
  [SearchMatchField.Idiomas]: "Idiomas",
  [SearchMatchField.TipoAtuacao]: "Tipo de atuação",
  [SearchMatchField.LinkedinBio]: "Bio do LinkedIn",
  [SearchMatchField.LinkedinExperiencias]: "Experiências no LinkedIn",
  [SearchMatchField.LinkedinSkills]: "Competências no LinkedIn",
  [SearchMatchField.Portfolio]: "Portfólio",
};

/** Palavras em sequência com as formas aceitas (RPC search_term_forms), como a busca do servidor. */
export interface SearchWords {
  forms: string[][];
  /** Sem "Palavra exata" a última palavra vale pelo começo: "design" acha "designer". */
  lastWordByStart: boolean;
}

/** Texto: trecho em qualquer lugar, para as agências e os campos de contato. */
export type SearchTerm = string | SearchWords;

export interface FreelancerSearchReason {
  field: SearchMatchField;
  snippet: string;
}

export interface FreelancerSearchMatch {
  /** Termo a marcar no nome: vale também em "somente nome". */
  nameTerm: string;
  /** Termo a marcar nos demais campos: vazio em "somente nome". */
  fieldTerm: SearchTerm;
  reasons: FreelancerSearchReason[];
}

export interface HighlightPart {
  text: string;
  matched: boolean;
}

const SEARCH_MATCH_FIELDS = new Set<string>(Object.values(SearchMatchField));

export function parseSearchMatchField(value: string): SearchMatchField | null {
  return SEARCH_MATCH_FIELDS.has(value) ? (value as SearchMatchField) : null;
}

function normalizeForSearch(text: string): string {
  return text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

function buildMatcher(term: SearchTerm): RegExp | null {
  if (typeof term === "string") {
    const target = normalizeForSearch(term.trim());
    return target ? new RegExp(target.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g") : null;
  }
  if (term.forms.length === 0) return null;
  const words = term.forms.map((forms) => `(?:${forms.join("|")})`).join("[^a-z0-9]+");
  return new RegExp(`(?<![a-z0-9])${words}${term.lastWordByStart ? "" : "(?![a-z0-9])"}`, "g");
}

export function containsSearchTerm(text: string | null | undefined, term: SearchTerm): boolean {
  const matcher = buildMatcher(term);
  return !!text && !!matcher && matcher.test(normalizeForSearch(text));
}

/** Corta o texto nas ocorrências do termo, sem olhar acento nem caixa, como a busca do servidor. */
export function splitByTerm(text: string, term: SearchTerm): HighlightPart[] {
  const matcher = buildMatcher(term);
  if (!matcher) return [{ text, matched: false }];

  const characters = Array.from(text.normalize("NFC"));
  const originOfNormalized: number[] = [];
  let normalized = "";
  characters.forEach((character, index) => {
    const normalizedCharacter = normalizeForSearch(character);
    normalized += normalizedCharacter;
    for (let offset = 0; offset < normalizedCharacter.length; offset += 1) originOfNormalized.push(index);
  });

  const parts: HighlightPart[] = [];
  let cursor = 0;
  for (const match of normalized.matchAll(matcher)) {
    const start = originOfNormalized[match.index];
    const end = originOfNormalized[match.index + match[0].length - 1] + 1;
    if (start > cursor) parts.push({ text: characters.slice(cursor, start).join(""), matched: false });
    parts.push({ text: characters.slice(start, end).join(""), matched: true });
    cursor = end;
  }
  if (parts.length === 0) return [{ text, matched: false }];
  if (cursor < characters.length) parts.push({ text: characters.slice(cursor).join(""), matched: false });
  return parts;
}

/** A cidade só vira motivo quando o local mostrado no card não tem o termo. */
export function reasonsHiddenFromCard(
  reasons: FreelancerSearchReason[],
  term: SearchTerm,
  visibleLocation: string | null,
): FreelancerSearchReason[] {
  return reasons.filter(
    (reason) => reason.field !== SearchMatchField.Cidade || !containsSearchTerm(visibleLocation, term),
  );
}
